import { useEffect, useState } from "react";
import { databases, DATABASE_ID, COLLECTIONS } from "../lib/appwriteClient";
import { ID, Query } from "appwrite";
import { Plus, Trash2, Save, AlertCircle, CheckCircle } from "lucide-react";

export function ScoreManager() {
  const [events, setEvents] = useState([]);
  const [scores, setScores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const [formData, setFormData] = useState({
    event_id: "",
    winner_name: "",
    batch_name: "",
    score: "",
    position: 1,
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const eventsRes = await databases.listDocuments(
        DATABASE_ID,
        COLLECTIONS.EVENTS,
        [Query.equal("is_active", true), Query.orderAsc("order")],
      );

      const scoresRes = await databases.listDocuments(
        DATABASE_ID,
        COLLECTIONS.SCORES,
        [Query.orderDesc("uploaded_at")],
      );

      setEvents(eventsRes.documents || []);
      setScores(scoresRes.documents || []);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);

    try {
      await databases.createDocument(
        DATABASE_ID,
        COLLECTIONS.SCORES,
        ID.unique(),
        {
          ...formData,
          uploaded_at: new Date().toISOString(),
        },
      );

      setMessage({ type: "success", text: "Score added successfully!" });

      setFormData({
        event_id: "",
        winner_name: "",
        batch_name: "",
        score: "",
        position: 1,
      });

      fetchData();
    } catch (error) {
      console.error(error);
      setMessage({ type: "error", text: "Failed to add score." });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this score?")) return;

    try {
      await databases.deleteDocument(DATABASE_ID, COLLECTIONS.SCORES, id);

      setMessage({ type: "success", text: "Score deleted successfully!" });
      fetchData();
    } catch (error) {
      console.error(error);
      setMessage({ type: "error", text: "Failed to delete score." });
    }
  };

  const getEventName = (eventId) => {
    return events.find((e) => e.$id === eventId)?.name || "Unknown Event";
  };

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div className="space-y-8">
      {message && (
        <div
          className={`rounded-lg p-4 flex gap-3 ${
            message.type === "success"
              ? "bg-green-50 border border-green-200"
              : "bg-red-50 border border-red-200"
          }`}
        >
          {message.type === "success" ? (
            <CheckCircle className="w-5 h-5 text-green-600" />
          ) : (
            <AlertCircle className="w-5 h-5 text-red-600" />
          )}
          <p>{message.text}</p>
        </div>
      )}

      {/* FORM */}
      <div className="from-[#00b477] to-[#007a55] p-6 rounded-lg border">
        <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
          <Plus className="w-5 h-5" />
          Add New Score
        </h3>

        <form onSubmit={handleSubmit} className="space-y-4">
          <select
            value={formData.event_id}
            onChange={(e) =>
              setFormData({ ...formData, event_id: e.target.value })
            }
            required
          >
            <option value="">Select event</option>
            {events.map((event) => (
              <option key={event.$id} value={event.$id}>
                {event.name}
              </option>
            ))}
          </select>

          <input
            type="text"
            placeholder="Winner name"
            value={formData.winner_name}
            onChange={(e) =>
              setFormData({ ...formData, winner_name: e.target.value })
            }
            required
          />

          <input
            type="text"
            placeholder="Batch"
            value={formData.batch_name}
            onChange={(e) =>
              setFormData({ ...formData, batch_name: e.target.value })
            }
          />

          <input
            type="text"
            placeholder="Score"
            value={formData.score}
            onChange={(e) =>
              setFormData({ ...formData, score: e.target.value })
            }
          />

          <select
            value={formData.position}
            onChange={(e) =>
              setFormData({
                ...formData,
                position: parseInt(e.target.value),
              })
            }
          >
            {[1, 2, 3, 4, 5].map((p) => (
              <option key={p} value={p}>
                {p} place
              </option>
            ))}
          </select>

          <button disabled={saving}>
            {saving ? "Saving..." : "Add Score"}
          </button>
        </form>
      </div>

      {/* LIST */}
      <div>
        <h3 className="text-xl font-bold mb-4">Current Scores</h3>

        {scores.map((score) => (
          <div key={score.$id} className="p-4 border rounded mb-2">
            <div className="flex justify-between">
              <div>
                <strong>{score.winner_name}</strong>
                <div>{getEventName(score.event_id)}</div>
                <div>{score.score}</div>
              </div>

              <button onClick={() => handleDelete(score.$id)}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
